/**
 * Enemy game logic state.
 * Pure data — no Phaser dependencies.
 */
export interface IEnemyLogicState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: 'left' | 'right';
  hp: number;
  maxHp: number;
  onGround: boolean;
  state: EnemyState;
  patrolDirection: number;
  patrolRange: number;
  patrolStartX: number;
  detectionRange: number;
  attackRange: number;
  damage: number;
  speed: number;
  isStunned: boolean;
  stunTimer: number;
  isAttacking: boolean;
  attackTimer: number;
  attackPhase: 'windup' | 'strike' | 'cooldown';
  attackWindupTime: number;
  attackStrikeTime: number;
  attackCooldownTime: number;
  hasBat: boolean;
  deathTimer: number;
  lastPlayerX: number;
  lastPlayerY: number;
  playerDetected: boolean;
  edgeCheckDistance: number;
  /** Playable world bounds. The enemy bounces back if it tries to leave. */
  worldMinX: number;
  worldMaxX: number;
  /** Allow the enemy to leave the world bounds (default false). */
  canLeaveWorld: boolean;
}

export type EnemyState = 'patrol' | 'detect' | 'chase' | 'attack' | 'return' | 'hurt' | 'dead';

export function createEnemyLogicState(
  x: number,
  y: number,
  enemyType: EnemyType
): IEnemyLogicState {
  const typeData = ENEMY_DATA[enemyType];
  return {
    x, y,
    vx: 0, vy: 0,
    facing: 'right',
    hp: typeData.hp,
    maxHp: typeData.hp,
    onGround: false,
    state: 'patrol',
    patrolDirection: 1,
    patrolRange: typeData.patrolRange,
    patrolStartX: x,
    detectionRange: typeData.detectionRange,
    attackRange: typeData.attackRange,
    damage: typeData.damage,
    speed: typeData.speed,
    isStunned: false,
    stunTimer: 0,
    isAttacking: false,
    attackTimer: 0,
    attackPhase: 'windup',
    attackWindupTime: typeData.hasBat ? 400 : 0,
    attackStrikeTime: typeData.hasBat ? 200 : 0,
    attackCooldownTime: typeData.hasBat ? 600 : 0,
    hasBat: typeData.hasBat,
    deathTimer: 0,
    lastPlayerX: x,
    lastPlayerY: y,
    playerDetected: false,
    edgeCheckDistance: 10,
    worldMinX: 0,
    worldMaxX: 0,
    canLeaveWorld: false,
  };
}

export type EnemyType = 'pcc' | 'fmc' | 'ujc' | 'ujc-bat';

export interface IEnemyTypeData {
  width: number;
  height: number;
  speed: number;
  hp: number;
  damage: number;
  patrolRange: number;
  detectionRange: number;
  attackRange: number;
  hasBat: boolean;
  batChance?: number;
}

export const ENEMY_DATA: Record<EnemyType, IEnemyTypeData> = {
  pcc: {
    width: 50, height: 100, speed: 0.6, hp: 1, damage: 20,
    patrolRange: 100, detectionRange: 200, attackRange: 40, hasBat: false,
  },
  fmc: {
    width: 54, height: 104, speed: 1.1, hp: 1, damage: 20,
    patrolRange: 120, detectionRange: 220, attackRange: 45, hasBat: false,
  },
  ujc: {
    width: 52, height: 106, speed: 1.6, hp: 1, damage: 20,
    patrolRange: 140, detectionRange: 250, attackRange: 50, hasBat: false,
  },
  'ujc-bat': {
    width: 52, height: 106, speed: 2.1, hp: 2, damage: 20,
    patrolRange: 140, detectionRange: 280, attackRange: 60, hasBat: true,
    batChance: 0.35,
  },
};

export function createEnemyWithRng(
  x: number,
  y: number,
  rng: { nextFloat(): number },
  batChance: number = 0.35
): { type: EnemyType; state: IEnemyLogicState } {
  const rawType: EnemyType = rng.nextFloat() < batChance ? 'ujc-bat' : 'ujc';
  const type: EnemyType = rawType;
  return {
    type,
    state: createEnemyLogicState(x, y, type),
  };
}
